import React, { useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import ServiceCategoryTooltip from "./ServiceCategoryTooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { tooltipContent } from "../utils/constants";
import { findRfiColorMeta } from "../utils/serviceCategoriesColors";
import {
  DEFAULT_RFI_BADGE_STYLE,
  getRfiCapabilityBadgeDisplayName,
} from "../utils/rfiCatalogServiceNames";
import { replaceRfiTerminologyForDisplay } from "../utils/apiResponseRfiToVqqDisplay";

const Capabilities = ({
  services,
  icon,
  isExpanded,
  setShowChevron,
  showLimited,
  name,
  showTooltips = true,
  applyRfiCatalogCustomLabels = false,
  badgesPerRow,
}) => {
  const { rfis } = useSelector((state) => state.rfisColors);
  const [badgeStyles, setBadgeStyles] = useState([]);
  const servicesCopy = [...services];
  const [isMultiLine, setIsMultiLine] = useState(false);
  const serviceRefs = useRef([]);

  const sortedServices = servicesCopy?.sort((a, b) => {
    if (a?.rfi_name === "Core Capabilities") return -1;
    if (b?.rfi_name === "Core Capabilities") return 1;
    return a?.rfi_name.length - b?.rfi_name.length;
  });

  const resolveBadgeStyle = (rfi) => {
    let match = rfis?.find(
      (item) => String(item.rfi_id) === String(rfi?.rfi_id)
    );
    if (!match && rfi?.rfi_name) {
      match = rfis?.find(
        (item) =>
          (item.rfi_name || "").toLowerCase() ===
          (rfi.rfi_name || "").toLowerCase()
      );
    }
    if (!match && rfi?.rfi_name) {
      const meta = findRfiColorMeta(rfi.rfi_name, rfis);
      if (meta) {
        return {
          bgColor: meta.bg,
          textColor: meta.text,
          borderColor: meta.border,
        };
      }
    }
    if (match) {
      return {
        bgColor: match.rfi_color_code_bg,
        textColor: match.rfi_color_code_text,
        borderColor: match.rfi_color_code_border,
      };
    }
    return DEFAULT_RFI_BADGE_STYLE;
  };

  const mapRfisColors = () => {
    if (sortedServices && Array.isArray(services)) {
      const styles = sortedServices.map((rfi) => resolveBadgeStyle(rfi));
      setBadgeStyles(styles);
    }
  };

  useEffect(() => {
    mapRfisColors();
  }, [services, rfis]);

  useEffect(() => {
    if (serviceRefs.current.length > 0) {
      const isAnyMultiLine = serviceRefs.current.some((ref) => {
        if (!ref) return false;
        const lineHeight = parseFloat(getComputedStyle(ref).lineHeight);
        return ref.scrollHeight > lineHeight * 1.5;
      });
      setIsMultiLine(isAnyMultiLine);
    }
  }, [services]);

  const maxVisibleTags = name?.length >= 30 ? 1 : 3;
  const remainingServices = sortedServices?.slice(maxVisibleTags);

  useEffect(() => {
    if (showLimited) {
      setShowChevron(remainingServices.length > 0);
    }
  }, [showLimited, remainingServices.length]);

  const renderBadge = (item, index) => {
    const rawLabel =
      item?.rfi_name ||
      (typeof item?.services === "string" ? item.services : undefined) ||
      item?.geographical_region ||
      item?.service_compliance_type ||
      item?.indication ||
      item?.therapeutic_area ||
      item?.diversity_classification;

    const badgeBase = applyRfiCatalogCustomLabels
      ? getRfiCapabilityBadgeDisplayName(rawLabel)
      : rawLabel;
    const displayName =
      typeof badgeBase === "string"
        ? replaceRfiTerminologyForDisplay(badgeBase)
        : badgeBase;

    const styles = badgeStyles[index] || {};

    return (
      <div key={index} ref={(el) => (serviceRefs.current[index] = el)}>
        <span
          style={{
            backgroundColor: styles.bgColor,
            color: styles.textColor,
            boxShadow: `inset 0 0 0 1px ${styles.borderColor}1A`, // Simulates ring-*/* effect (10% opacity)
          }}
          className="inline-flex items-center rounded px-2 text-xs font-medium py-1 space-x-2"
        >
          <span>{displayName}</span>
          {icon && rawLabel === "Core Capabilities" && (
            <Tooltip title={tooltipContent} arrow placement="bottom">
              <span>
                <InformationCircleIcon className="w-5 h-5 text-center" />
              </span>
            </Tooltip>
          )}
          {showTooltips && rawLabel !== "Core Capabilities" && (
            <ServiceCategoryTooltip name={rawLabel} />
          )}
        </span>
      </div>
    );
  };

  const visibleEntries =
    sortedServices?.reduce((acc, item, index) => {
      if (showLimited && index >= maxVisibleTags && !isExpanded) return acc;
      acc.push({ item, index });
      return acc;
    }, []) ?? [];

  const useRowGroups =
    typeof badgesPerRow === "number" && badgesPerRow > 0 && visibleEntries.length > 0;

  if (useRowGroups) {
    const rows = [];
    for (let i = 0; i < visibleEntries.length; i += badgesPerRow) {
      rows.push(visibleEntries.slice(i, i + badgesPerRow));
    }
    return (
      <div className="flex flex-col gap-y-1.5">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="flex flex-wrap items-center gap-x-1 gap-y-1"
          >
            {row.map(({ item, index }) => renderBadge(item, index))}
          </div>
        ))}
        {showLimited && !isExpanded && remainingServices.length > 0 && (
          <span className="inline-flex w-fit items-center rounded px-2 text-xs font-medium py-1 border space-x-2 bg-gray-300 text-gray-600">
            {` +${remainingServices.length} more`}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visibleEntries.map(({ item, index }) => renderBadge(item, index))}
      {showLimited && !isExpanded && remainingServices.length > 0 && (
        <span className="flex items-center rounded px-2 text-xs font-medium py-1 border space-x-2 bg-gray-300 text-gray-600">
          {` +${remainingServices.length} more`}
        </span>
      )}
    </div>
  );
};

export default Capabilities;
